import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface MarketingChannel {
  name: string;
  priority: "high" | "medium" | "low";
  audienceMatch: number;
  contentTypes: string[];
  postingFrequency: string;
  reasoning: string;
}

export interface CalendarActivity {
  day: string;
  task: string;
  channel: string;
}

export interface CalendarWeek {
  week: number;
  theme: string;
  activities: CalendarActivity[];
  milestone?: string;
}

export interface Campaign {
  name: string;
  objective: "awareness" | "engagement" | "conversion";
  channels: string[];
  duration: string;
  description: string;
  keyMessages: string[];
}

export interface SEOStrategy {
  primaryKeywords: string[];
  longTailKeywords: string[];
  contentPillars: string[];
}

export interface SocialProfile {
  bio?: string;
  headline?: string;
  summary?: string;
  contentThemes?: string[];
  hashtagStrategy?: string[];
}

export interface ContentIdea {
  type: "blog" | "social" | "video" | "email";
  title?: string;
  platform?: string;
  concept?: string;
  outline?: string;
  format?: string;
}

export interface MarketingPlan {
  overview: {
    mission: string;
    targetAudience: string;
    uniqueValue: string;
  };
  channels: MarketingChannel[];
  calendar: CalendarWeek[];
  campaigns: Campaign[];
  seoStrategy: SEOStrategy;
  socialProfiles: {
    instagram?: SocialProfile;
    linkedin?: SocialProfile;
    twitter?: SocialProfile;
    facebook?: SocialProfile;
    tiktok?: SocialProfile;
  };
  contentIdeas: ContentIdea[];
  generatedAt?: any;
}

interface UseMarketingPlanResult {
  isLoading: boolean;
  isGeneratingImage: boolean;
  isGeneratingContent: boolean;
  error: string | null;
  plan: MarketingPlan | null;
  generatedImage: string | null;
  generatedContent: string | null;
  generatePlan: (projectId: string) => Promise<void>;
  generateImage: (
    projectId: string,
    imageType: string,
    customPrompt?: string
  ) => Promise<string | null>;
  generateContent: (
    projectId: string,
    contentType: string,
    platform?: string,
    topic?: string
  ) => Promise<string | null>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for AI-powered marketing plan generation
 */
export function useMarketingPlan(): UseMarketingPlanResult {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState<MarketingPlan | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setPlan(null);
    setGeneratedImage(null);
    setGeneratedContent(null);
  }, []);

  const generatePlan = useCallback(async (projectId: string): Promise<void> => {
    if (!projectId) {
      setError("Project ID is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const generateFunction = httpsCallable<
        { projectId: string },
        { success: boolean; plan: MarketingPlan }
      >(functions, "generateMarketingPlan");

      const result = await generateFunction({ projectId });
      setPlan(result.data.plan);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to generate marketing plan";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateImage = useCallback(
    async (
      projectId: string,
      imageType: string,
      customPrompt?: string
    ): Promise<string | null> => {
      if (!projectId || !imageType) {
        setError("Project ID and image type are required");
        return null;
      }

      setIsGeneratingImage(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateFunction = httpsCallable<
          { projectId: string; imageType: string; customPrompt?: string },
          { success: boolean; imageUrl: string }
        >(functions, "generateSocialImages");

        const result = await generateFunction({
          projectId,
          imageType,
          customPrompt,
        });

        setGeneratedImage(result.data.imageUrl);
        return result.data.imageUrl;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate image";
        setError(errorMessage);
        return null;
      } finally {
        setIsGeneratingImage(false);
      }
    },
    []
  );

  const generateContent = useCallback(
    async (
      projectId: string,
      contentType: string,
      platform?: string,
      topic?: string
    ): Promise<string | null> => {
      if (!projectId || !contentType) {
        setError("Project ID and content type are required");
        return null;
      }

      setIsGeneratingContent(true);
      setError(null);

      try {
        const functions = getFunctions();
        const generateFunction = httpsCallable<
          {
            projectId: string;
            contentType: string;
            platform?: string;
            topic?: string;
          },
          { success: boolean; content: string }
        >(functions, "generateMarketingContent");

        const result = await generateFunction({
          projectId,
          contentType,
          platform,
          topic,
        });

        setGeneratedContent(result.data.content);
        return result.data.content;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate content";
        setError(errorMessage);
        return null;
      } finally {
        setIsGeneratingContent(false);
      }
    },
    []
  );

  return {
    isLoading,
    isGeneratingImage,
    isGeneratingContent,
    error,
    plan,
    generatedImage,
    generatedContent,
    generatePlan,
    generateImage,
    generateContent,
    clearError,
    reset,
  };
}
